import { useEffect, useState } from "react";
import { api, formatErr } from "@/lib/api";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Textarea } from "@/components/ui/textarea";
import { Badge } from "@/components/ui/badge";
import { Dialog, DialogContent, DialogDescription, DialogHeader, DialogTitle } from "@/components/ui/dialog";
import { Building2, Pencil } from "lucide-react";
import { toast } from "sonner";

function Field({ label, value, testid }) {
  return (
    <div>
      <div className="text-[10px] uppercase tracking-[0.2em] text-muted-foreground mb-1">{label}</div>
      <div className="text-sm font-medium" data-testid={testid}>{value || "—"}</div>
    </div>
  );
}

export default function OrganisationProfile() {
  const [org, setOrg] = useState(null);
  const [open, setOpen] = useState(false);
  const [form, setForm] = useState({ address: "", phone: "", email: "" });
  const [busy, setBusy] = useState(false);

  const load = () => api.get("/establishment/organisation")
    .then(r => setOrg(r.data)).catch(e => toast.error(formatErr(e)));
  useEffect(() => { load(); }, []);

  const openEdit = () => {
    setForm({ address: org.address || "", phone: org.phone || "", email: org.email || "" });
    setOpen(true);
  };
  const save = async () => {
    setBusy(true);
    try {
      await api.patch("/establishment/organisation", form);
      toast.success("Organisation updated");
      setOpen(false); load();
    } catch (e) { toast.error(formatErr(e)); }
    finally { setBusy(false); }
  };

  if (!org) return <div className="p-8 text-muted-foreground">Loading…</div>;

  return (
    <div className="p-8 max-w-4xl" data-testid="org-profile-page">
      <div className="flex items-end justify-between mb-6 flex-wrap gap-3">
        <div>
          <div className="text-primary text-xs uppercase tracking-[0.3em] mb-2 flex items-center gap-2"><Building2 className="w-3 h-3"/> Org &amp; Access · Organisation</div>
          <h1 className="font-display font-black text-4xl uppercase tracking-tighter">{org.business_name}</h1>
          <p className="text-muted-foreground text-sm mt-1">Managed by the platform admin — you can update address, mobile and email</p>
        </div>
        <Button data-testid="org-edit-btn" onClick={openEdit}
          className="rounded-sm bg-primary text-primary-foreground uppercase tracking-[0.15em] text-xs">
          <Pencil className="w-3 h-3 mr-1"/> Edit
        </Button>
      </div>

      <div className="border border-border bg-card rounded-sm p-6" data-testid="org-profile-card">
        <div className="grid grid-cols-3 gap-x-6 gap-y-6">
          <Field label="Organisation Name" value={org.business_name} testid="org-field-name"/>
          <Field label="GSTIN" value={org.gstin} testid="org-field-gstin"/>
          <Field label="PAN" value={org.pan} testid="org-field-pan"/>
          <div className="col-span-3">
            <div className="text-[10px] uppercase tracking-[0.2em] text-muted-foreground mb-1">Address</div>
            <div className="text-sm font-medium" data-testid="org-field-address">{org.address || "—"}</div>
          </div>
          <Field label="Mobile" value={org.phone} testid="org-field-mobile"/>
          <Field label="Email" value={org.email} testid="org-field-email"/>
          <Field label="Plan" value={org.plan_name} testid="org-field-plan"/>
          <div>
            <div className="text-[10px] uppercase tracking-[0.2em] text-muted-foreground mb-1">Status</div>
            <Badge data-testid="org-field-status"
              variant={org.status === "active" ? "default" : "destructive"}
              className="rounded-sm text-[10px] uppercase tracking-wider">
              {org.status || "—"}
            </Badge>
          </div>
        </div>
      </div>

      <Dialog open={open} onOpenChange={setOpen}>
        <DialogContent className="rounded-sm bg-card border-border max-w-md" data-testid="org-edit-dialog">
          <DialogHeader>
            <DialogTitle className="font-display uppercase">{org.business_name}</DialogTitle>
            <DialogDescription className="text-xs">Update contact details. Name, GSTIN, PAN and Plan are read-only.</DialogDescription>
          </DialogHeader>
          <div className="space-y-3">
            <div className="grid grid-cols-2 gap-3">
              <div>
                <Label className="text-xs uppercase tracking-[0.15em] text-muted-foreground">GSTIN (read-only)</Label>
                <Input data-testid="org-edit-gstin-ro" value={org.gstin || "—"} disabled className="mt-1 rounded-sm bg-secondary border-border font-mono opacity-60"/>
              </div>
              <div>
                <Label className="text-xs uppercase tracking-[0.15em] text-muted-foreground">PAN (read-only)</Label>
                <Input data-testid="org-edit-pan-ro" value={org.pan || "—"} disabled className="mt-1 rounded-sm bg-secondary border-border font-mono opacity-60"/>
              </div>
            </div>
            <div>
              <Label className="text-xs uppercase tracking-[0.15em] text-muted-foreground">Organisation Name (read-only)</Label>
              <Input data-testid="org-edit-name-ro" value={org.business_name || "—"} disabled className="mt-1 rounded-sm bg-secondary border-border opacity-60"/>
            </div>
            <div>
              <Label className="text-xs uppercase tracking-[0.15em] text-muted-foreground">Address</Label>
              <Textarea data-testid="org-edit-address" value={form.address} onChange={(e) => setForm({ ...form, address: e.target.value })} rows={2} className="mt-1 rounded-sm bg-secondary border-border"/>
            </div>
            <div className="grid grid-cols-2 gap-3">
              <div>
                <Label className="text-xs uppercase tracking-[0.15em] text-muted-foreground">Mobile</Label>
                <Input data-testid="org-edit-mobile" value={form.phone} onChange={(e) => setForm({ ...form, phone: e.target.value })} className="mt-1 rounded-sm bg-secondary border-border font-mono"/>
              </div>
              <div>
                <Label className="text-xs uppercase tracking-[0.15em] text-muted-foreground">Email</Label>
                <Input data-testid="org-edit-email" type="email" value={form.email} onChange={(e) => setForm({ ...form, email: e.target.value })} className="mt-1 rounded-sm bg-secondary border-border"/>
              </div>
            </div>
            <Button data-testid="org-edit-save" onClick={save} disabled={busy} className="w-full rounded-sm bg-primary text-primary-foreground uppercase tracking-[0.15em]">
              {busy ? "Saving…" : "Save Changes"}
            </Button>
          </div>
        </DialogContent>
      </Dialog>
    </div>
  );
}
